import { Inches } from "@/libs/units/length";
import { getEquipmentCrossSectionFt2 } from "@/libs/calculations/pressureDropCorrelations";

/**
 * Helper function to find equipment tubing at a specific depth
 */
export function findEquipmentTubingAtDepth(
  equipmentTubingsOrder: string[],
  equipmentTubingsData: Array<{
    id: string;
    length_feet?: number | null;
    r_tubing?: { interior_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>,
  targetDepthFt: number,
  startDepthFt: number = 0
): number | null {
  let cumulativeDepth = startDepthFt;
  for (const tubingId of equipmentTubingsOrder) {
    const tubing = equipmentTubingsData.find(t => t.id === tubingId);
    if (!tubing || tubing.journal_deleted_at) continue;

    const tubingLength = tubing.length_feet || 0;
    const tubingEndDepth = cumulativeDepth + tubingLength;

    if (targetDepthFt >= cumulativeDepth && targetDepthFt <= tubingEndDepth) {
      return tubing.r_tubing?.interior_diameter_inches || null;
    }
    cumulativeDepth = tubingEndDepth;
  }
  return null;
}

/**
 * Helper function to get equipment tubing internal diameter at pump discharge depth
 */
export function getEquipmentTubingIDAtPumpDischargeInches(
  useYTool: boolean,
  equipmentTubingOrder: string[] | null | undefined,
  equipmentTubingsData: Array<{
    id: string;
    length_feet?: number | null;
    r_tubing?: { interior_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>,
  bottomOfPumpsDepthFt: number,
  startDepthFt: number
): number | null {
  if (!useYTool || !equipmentTubingOrder || equipmentTubingOrder.length === 0) {
    return null;
  }
  const order = equipmentTubingOrder as string[];
  return findEquipmentTubingAtDepth(
    order,
    equipmentTubingsData,
    bottomOfPumpsDepthFt,
    startDepthFt
  );
}

/**
 * Helper function to find bypass tubing at a specific depth
 */
export function findBypassTubingAtDepth(
  bypassTubingsOrder: string[],
  bypassTubingsData: Array<{
    id: string;
    length_feet?: number | null;
    r_tubing?: { outer_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>,
  targetDepthFt: number,
  startDepthFt: number = 0
): number {
  let cumulativeDepth = startDepthFt;
  for (const tubingId of bypassTubingsOrder) {
    const tubing = bypassTubingsData.find(t => t.id === tubingId);
    if (!tubing || tubing.journal_deleted_at) continue;

    const tubingLength = tubing.length_feet || 0;
    const tubingEndDepth = cumulativeDepth + tubingLength;

    if (targetDepthFt >= cumulativeDepth && targetDepthFt <= tubingEndDepth) {
      return tubing.r_tubing?.outer_diameter_inches || 0;
    }
    cumulativeDepth = tubingEndDepth;
  }
  return 0;
}

/**
 * Helper function to calculate bypass tubing area from outer diameter
 */
export function calculateBypassTubingAreaFt2(odInches: number): number {
  if (odInches === 0) return 0;
  const PI = Math.PI;
  const radiusFt = Inches.toStandardUnit(odInches / 2);
  return PI * radiusFt ** 2;
}

/**
 * Helper function to get bypass tubing OD at motor depth
 */
export function getBypassTubingODAtMotorInches(
  useYTool: boolean,
  bypassTubingOrder: string[] | null | undefined,
  bypassTubingsData: Array<{
    id: string;
    length_feet?: number | null;
    r_tubing?: { outer_diameter_inches?: number | null } | null;
    journal_deleted_at?: unknown;
  }>,
  bottomOfMotorDepthFt: number,
  startDepthFt: number
): number {
  if (!useYTool || !bypassTubingOrder) return 0;
  const order = bypassTubingOrder;
  return findBypassTubingAtDepth(
    order,
    bypassTubingsData,
    bottomOfMotorDepthFt,
    startDepthFt
  );
}

/**
 * Helper function to calculate motor cross-section with bypass tubing adjustment
 */
export function calculateMotorCrossSectionFt2(
  casingDiameterIn: number,
  shroudIDInches: number,
  motorODInches: number,
  bypassTubingAreaFt2: number
): number {
  if (motorODInches === null || motorODInches === 0) return 0;
  // If we have bypass tubing, calculate the area it occupies and subtract from motor cross-section
  if (bypassTubingAreaFt2 !== 0) {
    if (shroudIDInches) {
      return getEquipmentCrossSectionFt2(shroudIDInches, motorODInches);
    } else {
      // If no shroud, assume bypass tubing occupies the area of the motor OD
      const baseCrossSection = getEquipmentCrossSectionFt2(
        casingDiameterIn,
        motorODInches
      );
      return Math.max(0, baseCrossSection - bypassTubingAreaFt2);
    }
  } else {
    const outerDiameterIn = shroudIDInches || casingDiameterIn;
    return getEquipmentCrossSectionFt2(outerDiameterIn, motorODInches);
  }
}
